import type { TableSpec, JoinSpec } from "../../lib/funnel/SchemaDiagram";

// Schema diagram config for SIGNUP_FUNNEL_SQL. Spine is public.user_roles
// (status='active'); one row per (user, company). Activity aggregating CTEs
// attribute to the workspace where it happened. Demo path joins via email
// against reporting.demo (is_demo=true).

export const SIGNUP_FUNNEL_TABLES: TableSpec[] = [
  // Centre: spine
  { id: "user_roles", schema: "public", alias: "ur", label: "user_roles", x: 700, y: 600, columns: [
    { name: "user_external_id" },
    { name: "company_external_id" },
    { name: "role" },
    { name: "status", note: "= 'active'" },
  ] },

  // Identity ring
  { id: "users", schema: "public", alias: "u", label: "users", x: 920, y: 240, columns: [
    { name: "external_id" },
    { name: "email" },
    { name: "first_name / last_name" },
    { name: "created_at_utc", note: "= signup_date_utc" },
    { name: "attribution / attribution_category" },
    { name: "metadata", note: "utm_source / medium / campaign / content / term" },
    { name: "fbc / gclid" },
  ] },
  { id: "companies", schema: "public", alias: "c", label: "companies", x: 1080, y: 600, columns: [
    { name: "external_id" },
    { name: "name" },
    { name: "country_code" },
  ] },
  { id: "company_billing", schema: "public", alias: "cb", label: "company_billing", x: 1080, y: 920, columns: [
    { name: "company_external_id" },
    { name: "billing_plan" },
    { name: "subscription_started_date_utc" },
  ] },
  { id: "billing_cte", schema: "cte", alias: "b", label: "billing", x: 1380, y: 600, columns: [
    { name: "company_external_id" },
    { name: "on_paid_plan", note: "bool_or plus/pro" },
    { name: "billing_plan / subscription_started_date_utc" },
  ] },
  { id: "company_size", schema: "cte", alias: "cs", label: "company_size", x: 1380, y: 880, columns: [
    { name: "company_external_id" },
    { name: "marloo_active_user_count", note: "DISTINCT users where active" },
  ] },

  // Activity CTEs (left side)
  { id: "transcripts", schema: "cte", alias: "t", label: "transcripts", x: 280, y: 280, columns: [
    { name: "user_external_id" },
    { name: "company_external_id" },
    { name: "transcript_count" },
    { name: "first_transcript_date / last_transcript_date" },
  ] },
  { id: "synopses", schema: "cte", alias: "s", label: "synopses", x: 280, y: 480, columns: [
    { name: "user_external_id" },
    { name: "company_external_id" },
    { name: "synopsis_count" },
    { name: "first_synopsis_date / last_synopsis_date" },
  ] },
  { id: "chats_cte", schema: "cte", alias: "ch", label: "chats", x: 280, y: 680, columns: [
    { name: "user_external_id / company_external_id" },
    { name: "chat_meeting_count", note: "FILTER meeting/synopsis FK" },
    { name: "chat_client_count", note: "FILTER client FK" },
    { name: "first_chat_meeting_date / first_chat_client_date" },
  ] },
  { id: "templates_cte", schema: "cte", alias: "ct", label: "custom_templates", x: 280, y: 880, columns: [
    { name: "user_external_id / company_external_id" },
    { name: "custom_template_count" },
    { name: "first_custom_template_date", note: "> signup + 5m" },
  ] },
  { id: "calendar_cte", schema: "cte", alias: "cf", label: "calendar_first", x: 280, y: 1080, columns: [
    { name: "user_external_id / company_external_id" },
    { name: "first_calendar_date" },
  ] },

  // Source tables for activity (left edge)
  { id: "meeting_transcription", schema: "public", alias: "mt", label: "meeting_transcription", x: 30, y: 320, columns: [
    { name: "user_external_id / company_external_id" },
    { name: "meeting_external_id", note: "→ meeting" },
    { name: "created_date_utc" },
    { name: "deleted_date_utc", note: "IS NULL" },
  ] },
  { id: "meeting_synopsis", schema: "public", alias: "ms", label: "meeting_synopsis", x: 30, y: 540, columns: [
    { name: "user_external_id / company_external_id" },
    { name: "meeting_external_id", note: "→ meeting" },
    { name: "created_date_utc" },
  ] },
  { id: "meeting", schema: "public", alias: "m", label: "meeting", x: 30, y: 800, columns: [
    { name: "external_id" },
    { name: "is_demo", note: "= false (excludes demo content)" },
    { name: "deleted_date_utc", note: "IS NULL" },
  ] },
  { id: "chat", schema: "public", alias: "c_raw", label: "chat", x: 30, y: 1020, columns: [
    { name: "user_external_id / company_external_id" },
    { name: "meeting_external_id / synopsis_external_id" },
    { name: "client_external_id" },
    { name: "deleted_date_utc", note: "IS NULL" },
  ] },
  { id: "template", schema: "public", alias: "t_raw", label: "template", x: 30, y: 1240, columns: [
    { name: "user_external_id / company_external_id" },
    { name: "created_date_utc", note: "> signup + 5m" },
    { name: "is_archived", note: "= false" },
  ] },
  { id: "calendar", schema: "public", alias: "cal", label: "calendar", x: 30, y: 1440, columns: [
    { name: "user_external_id / company_external_id" },
    { name: "created_date_utc", note: "MIN = first_calendar_date" },
  ] },

  // Demo attendance (bottom-right)
  { id: "demo", schema: "reporting", alias: "d", label: "demo", x: 1380, y: 280, columns: [
    { name: "external_id" },
    { name: "attendee_emails", note: "unnest" },
    { name: "follow_up_context", note: "is_demo=true" },
    { name: "organiser_email", note: "ILIKE '%@gomarloo.com'" },
    { name: "created_date_utc" },
  ] },
  { id: "demo_attendance", schema: "cte", alias: "da", label: "demo_attendance", x: 1380, y: 360, columns: [
    { name: "user_email", note: "lower(unnest)" },
    { name: "first_demo_date" },
    { name: "demo_count" },
  ] },
];

export const SIGNUP_FUNNEL_JOINS: JoinSpec[] = [
  // Direct joins around the spine
  { id: "ur-u", source: "user_roles", target: "users", kind: "direct", label: "user_external_id" },
  { id: "ur-c", source: "user_roles", target: "companies", kind: "direct", label: "company_external_id" },
  { id: "c-cb", source: "companies", target: "company_billing", kind: "direct", label: "company_external_id" },
  { id: "cb-b", source: "company_billing", target: "billing_cte", kind: "cte", label: "bool_or paid" },
  { id: "ur-cs", source: "user_roles", target: "company_size", kind: "cte", label: "DISTINCT users" },

  // Activity CTEs
  { id: "ur-t", source: "user_roles", target: "transcripts", kind: "cte", label: "(user, company)" },
  { id: "ur-s", source: "user_roles", target: "synopses", kind: "cte", label: "(user, company)" },
  { id: "ur-ch", source: "user_roles", target: "chats_cte", kind: "cte", label: "(user, company)" },
  { id: "ur-ct", source: "user_roles", target: "templates_cte", kind: "cte", label: "(user, company)" },
  { id: "ur-cf", source: "user_roles", target: "calendar_cte", kind: "cte", label: "(user, company)" },

  // CTE inputs
  { id: "mt-t", source: "meeting_transcription", target: "transcripts", kind: "direct", label: "JOIN" },
  { id: "ms-s", source: "meeting_synopsis", target: "synopses", kind: "direct", label: "JOIN" },
  { id: "mt-m", source: "meeting_transcription", target: "meeting", kind: "direct", label: "meeting_external_id" },
  { id: "ms-m", source: "meeting_synopsis", target: "meeting", kind: "direct", label: "meeting_external_id" },
  { id: "chat-ch", source: "chat", target: "chats_cte", kind: "direct", label: "FILTER split" },
  { id: "tpl-ct", source: "template", target: "templates_cte", kind: "direct", label: "JOIN" },
  { id: "cal-cf", source: "calendar", target: "calendar_cte", kind: "direct", label: "MIN(date)" },

  // Demo
  { id: "demo-da", source: "demo", target: "demo_attendance", kind: "cte", label: "unnest attendees" },
  { id: "u-da", source: "users", target: "demo_attendance", kind: "email", label: "lower(email)" },
];
